import { useEffect, useRef } from 'react';
import { debounce } from '../utils/debounce';

export function useLocalStorage<T>(
  key: string,
  value: T,
  enabled: boolean,
  delayMs = 300,
  storageKind: 'local' | 'session' = 'local'
): void {
  const storageKindRef = useRef(storageKind);
  storageKindRef.current = storageKind;

  const debouncedWrite = useRef(
    debounce((k: unknown, v: unknown) => {
      try {
        const store =
          storageKindRef.current === 'session' ? sessionStorage : localStorage;
        store.setItem(k as string, JSON.stringify(v));
      } catch {
        // Storage unavailable
      }
    }, delayMs)
  ).current;

  useEffect(() => {
    if (!enabled) return;
    debouncedWrite(key, value);
  }, [key, value, enabled, debouncedWrite]);

  useEffect(() => {
    return () => debouncedWrite.cancel();
  }, [debouncedWrite]);
}

export function readLocalStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
